import { Injectable, computed, inject, signal } from '@angular/core';
import { HttpClient } from '@angular/common/http';
import { rxResource } from '@angular/core/rxjs-interop';
import type { FeatureKey } from '@zag/licensing/common';

interface EntitlementsResponse {
  features: FeatureKey[];
}

/**
 * Tenant feature entitlements for the UI (story 29-3 AC8): navigation
 * hiding and route guards read from here. The API fails towards
 * availability (grace mode returns the full catalogue), so an error or
 * missing data here also fails open — licence enforcement is server-side,
 * the UI only reduces noise.
 */
@Injectable({ providedIn: 'root' })
export class EntitlementsService {
  private readonly http = inject(HttpClient);
  private readonly reloadTrigger = signal(0);

  readonly entitlementsResource = rxResource<EntitlementsResponse, { trigger: number }>({
    params: () => ({ trigger: this.reloadTrigger() }),
    stream: () => this.http.get<EntitlementsResponse>('/api/v1/entitlements'),
  });

  private readonly featureSet = computed(() => {
    const value = this.entitlementsResource.value();
    return value ? new Set<FeatureKey>(value.features) : undefined;
  });

  /** True when licensed — or while entitlements are unknown (fail open, server enforces). */
  hasFeature(feature: FeatureKey): boolean {
    const features = this.featureSet();
    return features === undefined || features.has(feature);
  }

  /** Re-fetch after a 403 FEATURE_NOT_LICENSED response so stale nav disappears. */
  refresh(): void {
    this.reloadTrigger.update(count => count + 1);
  }
}
